/**
 * Order Status Colors
 * Single source of truth for biomarker status colors
 * These match the Tailwind config values in src/tailwind.config.js
 */

type OrderStatusKey =
    | 'IN_TRANSIT'
    | 'IN_PROCESS'
    | 'COMPLETE'
    | 'PARTIALLY_RESULTED'
    | 'ON_HOLD'
    | 'REJECTED'
    | 'PENDING'
    | 'UNKNOWN';

export const ORDER_STATUS_COLORS: Record<OrderStatusKey, string> = {
    IN_TRANSIT: '#42D4B0',
    IN_PROCESS: '#42D4B0',
    COMPLETE: '#4B5563',
    /** Pink/Red (#F15E87) */
    PARTIALLY_RESULTED: '#F15E87',
    /** Orange (#FFBE7A) */
    ON_HOLD: '#FFBE7A',
    /** Orange (#FFBE7A) */
    REJECTED: '#F15E87',
    /** Slate gray */
    PENDING: '#FFBE7A',
    UNKNOWN: '#9CA3AF',
};

/**
 * Biomarker Value Color
 * Dark teal color used for displaying biomarker numeric values
 */
export const BIOMARKER_VALUE_COLOR = '#294B5A';

/**
 * Get color for a biomarker status
 * @param status - The biomarker status
 * @returns The hex color string
 */
export const getStatusColor = (status?: string | null): string => {
    return ORDER_STATUS_COLORS[normalizeStatusKey(status)];
};

/**
 * Tailwind CSS class names for order status colors
 * Use these when you need Tailwind utility classes
 */
export const ORDER_STATUS_CLASSES: Record<OrderStatusKey, {text: string; bg: string; border: string}> = {
    IN_TRANSIT: {
        text: 'text-mld-status-normal',
        bg: 'bg-mld-status-normal',
        border: 'border-mld-status-normal',
    },
    IN_PROCESS: {
        text: 'text-mld-status-optimal',
        bg: 'bg-mld-status-optimal',
        border: 'border-mld-status-optimal',
    },
    COMPLETE: {
        text: 'text-mld-status-low',
        bg: 'bg-mld-status-low',
        border: 'border-mld-status-low',
    },
    PARTIALLY_RESULTED: {
        text: 'text-mld-status-high',
        bg: 'bg-mld-status-high',
        border: 'border-mld-status-high',
    },
    ON_HOLD: {
        text: 'text-mld-status-borderline',
        bg: 'bg-mld-status-borderline',
        border: 'border-mld-status-borderline',
    },
    REJECTED: {
        text: 'text-mld-status-elevated',
        bg: 'bg-mld-status-elevated',
        border: 'border-mld-status-elevated',
    },
    PENDING: {
        text: 'text-mld-status-optimal',
        bg: 'bg-mld-status-optimal',
        border: 'border-mld-status-optimal',
    },
    UNKNOWN: {
        text: 'text-gray-400',
        bg: 'bg-gray-100',
        border: 'border-gray-200',
    },
};

/**
 * Get Tailwind class name for a order status
 * @param status - The order status
 * @param variant - The CSS property (text, bg, border)
 * @returns The Tailwind class name
 */
export const getStatusClass = (status?: string | null, variant: 'text' | 'bg' | 'border' = 'text'): string => {
    const normalized = normalizeStatusKey(status);
    return ORDER_STATUS_CLASSES[normalized]?.[variant] || 'text-gray-400';
};

function normalizeStatusKey(status?: string | null): OrderStatusKey {
    if (!status) {
        return 'UNKNOWN';
    }

    const value = status.trim().toLowerCase();
    if (!value) {
        return 'UNKNOWN';
    }

    if (value.includes('transit')) {
        return 'IN_TRANSIT';
    }
    if (value.includes('process') || value.includes('progress')) {
        return 'IN_PROCESS';
    }
    if (value.includes('partial')) {
        return 'PARTIALLY_RESULTED';
    }
    if (value.includes('hold')) {
        return 'ON_HOLD';
    }
    if (value.includes('reject')) {
        return 'REJECTED';
    }
    if (value.includes('complete')) {
        return 'COMPLETE';
    }
    if (value.includes('pend') || value.includes('await')) {
        return 'PENDING';
    }

    return 'UNKNOWN';
}
